import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom'; 
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import API_BASE_URL from '../apiConfig'; // <--- NEW IMPORT
import '../App.css'; 

// --- CHANGED URLs HERE ---
const API_URL = `${API_BASE_URL}/generate-assessment`;
const PARSE_URL = `${API_BASE_URL}/parse-document`; 
// -------------------------

const LOADING_MESSAGES = [
  "Reading Job Description...",
  "Analyzing Resume for Gaps...",
  "Identifying Key Competencies...",
  "Drafting Scenario Questions...",
  "Finalizing Rubrics...",
  "Almost there..."
];

function AssessorPage() {
  const [jd, setJd] = useState("");
  const [resume, setResume] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); 
  const [loadingText, setLoadingText] = useState(LOADING_MESSAGES[0]);

  const { token, user, setUser } = useAuth();
  const navigate = useNavigate(); 

  useEffect(() => {
    let interval = null;
    if (loading) {
        let i = 0;
        setLoadingText(LOADING_MESSAGES[0]);
        interval = setInterval(() => {
            i = (i + 1) % LOADING_MESSAGES.length;
            setLoadingText(LOADING_MESSAGES[i]);
        }, 2500); 
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleClear = () => {
    if (jd || resume || jobTitle || candidateEmail || results) {
        if (!window.confirm("Are you sure you want to clear the form and start a new assessment?")) {
            return;
        }
    }
    setJd(""); setResume(""); setJobTitle(""); setCandidateEmail(""); setResults(null);
    document.getElementById('jd-upload').value = "";
    document.getElementById('resume-upload').value = "";
  };

  const handleSubmit = async () => {
    if (!jd || !resume) {
      alert("Please fill out both the Job Description and the Resume.");
      return;
    }
    if (!jobTitle || !candidateEmail) {
        alert("Please ensure both Job Title and Candidate Email are specified.");
        return;
    }
    if (user && user.credit_balance <= 0) {
        alert("Insufficient credits. Please purchase a new credit pack.");
        return;
    }
    
    setLoading(true);
    setResults(null); 

    try {
      const requestData = {
        job_description: jd,
        resume_text: resume,
        job_title: jobTitle,
        candidate_email: candidateEmail
      };

      const headers = { 'Authorization': `Bearer ${token}` };
      const response = await axios.post(API_URL, requestData, { headers });
      
      if (user) {
        setUser({ ...user, credit_balance: user.credit_balance - 1 });
      }

      navigate(`/success/${response.data.link_token}`);

    } catch (error) {
      console.error("Error calling API:", error);
      if (error.response && error.response.status === 402) {
        alert("Insufficient credits. Please purchase a new credit pack.");
      } else if (error.response && error.response.status === 401) {
        alert("Authentication failed. Please log out and log back in.");
      } else {
        alert("An error occurred while generating the assessment. Check the console.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e, targetSetter) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.docx')) {
        alert("Please upload a PDF, DOCX, or TXT file.");
        return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
        setLoading(true); 
        const response = await axios.post(PARSE_URL, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        targetSetter(response.data.text);
    } catch (err) {
        console.error("Parsing error:", err);
        alert("Failed to read file. Please copy-paste the text instead.");
    } finally {
        setLoading(false);
        e.target.value = null; 
    }
  };

  if (!user && !token) {
    return (
        <div className="App"><header className="App-header"><p>Please log in to use the Assessor tool.</p><Link to="/login" className="nav-link-button">Go to Login</Link></header></div>
    );
  }

  return (
    <div className="App">
      <Navbar />
      <header className="App-header">
        <h1>LuxerAssess</h1>

        <div className="form-container">
          
          <div className="input-row">
            <div className="input-group">
              <label>Job Title</label>
              <input 
                type="text"
                placeholder="e.g., Senior DevOps Engineer"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                disabled={loading}
                required 
              />
            </div>
            <div className="input-group">
              <label>Candidate Email</label>
              <input 
                type="email"
                placeholder="e.g., candidate@example.com"
                value={candidateEmail}
                onChange={(e) => setCandidateEmail(e.target.value)}
                disabled={loading}
                required 
              />
            </div>
          </div>

          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <h2>1. Paste Job Description</h2>
            <div className="file-upload-wrapper">
                <label htmlFor="jd-upload" className="secondary-button" style={{cursor: 'pointer', fontSize: '0.9rem', padding: '5px 10px'}}>📂 Upload PDF/Docx</label>
                <input id="jd-upload" type="file" accept=".pdf,.docx,.txt" style={{display: 'none'}} onChange={(e) => handleFileUpload(e, setJd)} disabled={loading} />
            </div>
          </div>
          <textarea placeholder="Paste the full job description here..." rows="10" value={jd} onChange={(e) => setJd(e.target.value)} disabled={loading} />

          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <h2>2. Paste Resume</h2>
            <div className="file-upload-wrapper">
                <label htmlFor="resume-upload" className="secondary-button" style={{cursor: 'pointer', fontSize: '0.9rem', padding: '5px 10px'}}>📂 Upload PDF/Docx</label>
                <input id="resume-upload" type="file" accept=".pdf,.docx,.txt" style={{display: 'none'}} onChange={(e) => handleFileUpload(e, setResume)} disabled={loading} />
            </div>
          </div>
          <textarea placeholder="Paste the candidate's resume text here..." rows="10" value={resume} onChange={(e) => setResume(e.target.value)} disabled={loading} />

          <div style={{display: 'flex', gap: '15px', marginTop: '20px'}}>
            <button 
                className="generate-button" 
                onClick={handleSubmit} 
                disabled={loading || (user && user.credit_balance <= 0)}
                style={{flex: 2}} 
            >
                {user && user.credit_balance <= 0 
                    ? "Insufficient Credits" 
                    : loading ? "Processing..." : "Generate Assessment"}
            </button>

            <button 
                className="secondary-button" 
                onClick={handleClear} 
                disabled={loading}
                style={{flex: 1, textAlign: 'center'}}
            >
                Clear Form
            </button>
          </div>
          
          {loading && (
             <div className="loader-container" style={{marginTop: '30px'}}>
                <div className="spinner"></div>
                <div className="loading-text-dynamic">{loadingText}</div>
             </div>
          )}

        </div>
      </header>
      <Footer />
    </div>
  );
}

export default AssessorPage;